import {Floating} from '@/components/motion/Floating';
import {Reveal} from '@/components/motion/Reveal';
import {Sparkle} from '@/components/motion/Sparkle';
import {RecapVideo} from '@/components/public/RecapVideo';
import {config} from '@/config';
import {asset} from '@/lib/asset';

const HIGHLIGHTS = [
  'Comida y bebida',
  'Moda',
  'Artesanías',
  'Belleza',
  'Hogar',
  'Mascotas',
  'Salud y bienestar',
] as const;

export function PublicPanel() {
  return (
    <div>
      {/* El evento */}
      <section aria-labelledby="evento-title" id="evento" className="scroll-mt-32">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:py-20">
          <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_1fr]">
            <div>
              <Reveal>
                <p className="overline-label text-amber">¿Qué es el Navimarket?</p>
                <h2 id="evento-title" className="mt-3 text-balance text-3xl font-bold text-white sm:text-4xl">
                  Un bazar navideño hecho de <span className="text-amber">negocios locales</span>.
                </h2>
                <p className="mt-5 max-w-xl text-pretty leading-relaxed text-cream/90">
                  Más de 200 comercios de toda la isla se juntan para abrir la temporada: comida, regalos, artesanías y
                  música, con pagos fáciles usando ATH Móvil. La entrada es libre — trae a la familia y estrena la
                  Navidad comprando local.
                </p>
              </Reveal>

              <Reveal delay={0.12} className="mt-8">
                <ul className="flex flex-wrap gap-2.5" aria-label="Lo que encontrarás">
                  {HIGHLIGHTS.map((h) => (
                    <li
                      key={h}
                      className="rounded-full border border-white/12 bg-white/5 px-4 py-1.5 text-sm text-cream"
                    >
                      {h}
                    </li>
                  ))}
                </ul>
              </Reveal>

              <Reveal delay={0.2} className="mt-10">
                <dl className="grid max-w-xl gap-x-8 gap-y-6 sm:grid-cols-2">
                  <div>
                    <dt className="overline-label text-cream/70">Fecha</dt>
                    <dd className="mt-1.5 font-bold text-white">{config.EVENT.date}</dd>
                  </div>
                  <div>
                    <dt className="overline-label text-cream/70">Horario</dt>
                    <dd className="mt-1.5 font-bold text-white">{config.EVENT.schedule}</dd>
                  </div>
                  <div>
                    <dt className="overline-label text-cream/70">Lugar</dt>
                    <dd className="mt-1.5 font-bold text-white">
                      {config.EVENT.venue}
                      <span className="block font-normal text-cream/80">{config.EVENT.city}</span>
                    </dd>
                  </div>
                  <div>
                    <dt className="overline-label text-cream/70">Entrada</dt>
                    <dd className="mt-1.5 font-bold text-amber">{config.EVENT.admission}</dd>
                  </div>
                  {/*
                    Estacionamiento vive aquí mientras el mapa siga oculto: era
                    información del mapa y sin él no aparecía en ningún lado.
                    Al volver EventMap, evaluar si se mueve de nuevo allá.
                  */}
                  <div className="sm:col-span-2">
                    <dt className="overline-label text-cream/70">Estacionamiento</dt>
                    <dd className="mt-1.5 leading-relaxed text-cream/90">
                      Estacionamientos del <span className="font-bold text-white">Centro de Convenciones</span> y de{' '}
                      <span className="font-bold text-white">Distrito T-Mobile</span>. Ambos cuentan con Parking
                      Checkout: paga tu boleto con <span className="font-bold text-white">ATH Móvil</span> sin tener que
                      ir a la caja.
                    </dd>
                  </div>
                </dl>
              </Reveal>
            </div>

            {/*
              Sólo en escritorio: apilado bajo el texto en móvil no aporta nada
              y alarga la página. Mismo criterio que el bodegón del hero y que
              el elemento del panel de vendors.
            */}
            <div className="relative mx-auto hidden w-full max-w-sm grid-cols-2 items-end gap-6 lg:grid">
              <Sparkle className="absolute -top-10 right-4" size={20} />
              <Sparkle className="absolute -left-8 bottom-6 opacity-70" size={13} delay={1.1} />
              <Floating
                src={asset('img/elements/domino.webp')}
                alt="Fichas de dominó con la bandera de Puerto Rico"
                className="h-36"
                depth={1}
                delay={0.2}
                drift={8}
              />
              <Floating
                src={asset('img/elements/bola.webp')}
                alt="Bola de Navidad naranja"
                className="h-44"
                depth={1.4}
                delay={0.35}
                drift={11}
                duration={5.6}
              />
              <Floating
                src={asset('img/elements/coquito.webp')}
                alt="Botella de coquito"
                className="h-44 justify-self-end"
                depth={1.2}
                delay={0.5}
                drift={9}
              />
              <Floating
                src={asset('img/elements/pastel.webp')}
                alt="Pastel puertorriqueño envuelto en hoja de plátano"
                className="h-32"
                depth={1.6}
                delay={0.65}
                drift={7}
              />
            </div>
          </div>
        </div>
      </section>

      {/*
        Recap 2025.

        Ocupa el lugar que tenía el mapa del evento: el mapa queda oculto hasta
        que el cliente confirme distribución y accesos (el componente sigue en
        src/components/public/EventMap.tsx, listo para volver). Mientras tanto
        esta franja hace el trabajo de antojar: cómo se vivió el año pasado.
      */}
      <section aria-labelledby="recap-title" id="recap" className="scroll-mt-28 py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <Reveal>
            <p className="overline-label text-amber">Recap 2025</p>
            <h2 id="recap-title" className="mt-3 text-3xl font-bold text-white sm:text-4xl">
              Así se vivió Navimarket
            </h2>
            <p className="mt-4 max-w-2xl text-pretty leading-relaxed text-cream/80">
              El pasado 14 de diciembre celebramos el evento más grande del año de ATH. Gracias a las miles de personas
              que nos acompañaron y apoyaron a los más de 500 comercios locales que dijeron presente. Este domingo, 15
              de noviembre, lo volvemos a hacer.
            </p>
          </Reveal>

          <Reveal delay={0.12} className="mt-8">
            <RecapVideo />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
